import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCompany } from "@/lib/company";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

export default function Invoice() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const { company } = useCompany();
  const { t, lang } = useLang();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (id) api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  }, [id]);

  if (loading) return null;
  if (!user) return <Navigate to="/connexion"/>;
  if (!order || !company) return <div className="p-12">{t("common.loading")}</div>;

  const subtotal = order.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const invoiceNum = `INV-${(order.reference || order.id.slice(0, 8)).replace(/^SKN-/, "")}`;
  const localeDate = lang === "en" ? "en-GB" : "fr-FR";
  const dateStr = new Date(order.created_at).toLocaleDateString(localeDate, { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="max-w-3xl mx-auto p-6 print:p-0" data-testid="invoice-page">
      <div className="print:hidden mb-6 flex gap-2">
        <Button onClick={() => window.print()} className="pill-btn bg-[#1A3322] rounded-full" data-testid="print-invoice"><Printer className="w-4 h-4 mr-2"/>{t("inv.print")}</Button>
      </div>

      <div className="bg-white border border-[#1A3322]/15 rounded-lg p-10 print:border-0 print:rounded-none">
        <div className="flex justify-between items-start mb-10">
          <div>
            <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/9b5fhq23_LOGO%20SUKACOOP.PNG" alt="SUKANACOOP" className="h-14 mb-3"/>
            <p className="heading font-extrabold text-2xl text-[#1A3322]">SUKANACOOP</p>
            <p className="text-xs text-[#4B5563] mt-1">{t("inv.marketplace_by")}</p>
            <div className="flex items-center gap-2 mt-1">
              <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/s0zrb57v_LOGO%20GNL%20SEC.jpg" alt="GNL" className="h-8 w-8 object-contain"/>
              <p className="font-semibold text-sm">{company.name}</p>
            </div>
          </div>
          <div className="text-right">
            <h1 className="heading font-extrabold text-3xl text-[#1A3322]">{t("inv.title")}</h1>
            <p className="text-sm text-[#4B5563] mt-1">{t("inv.number")} <span className="font-mono font-semibold text-[#111812]" data-testid="invoice-number">{invoiceNum}</span></p>
            <p className="text-sm text-[#4B5563]">{t("inv.issued_on")} <span className="font-semibold text-[#111812]">{dateStr}</span></p>
            <p className="text-xs mt-2 inline-block px-2 py-1 rounded-full font-bold uppercase tracking-wider" style={{background: order.payment_status === "paid" ? "#1A3322" : "#D98A2C", color: "white"}} data-testid="invoice-status">
              {order.payment_status === "paid" ? t("inv.paid") : t("inv.pending")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-10">
          <div>
            <p className="label-uppercase text-xs text-[#4B5563]">{t("inv.emitter")}</p>
            <p className="font-bold mt-1">{company.name}</p>
            <p className="text-sm">{t("inv.rccm")} <span className="font-mono">{company.rccm}</span></p>
            <p className="text-sm">{t("inv.tax_id")} <span className="font-mono">{company.tax_id}</span></p>
            <p className="text-sm">{company.address}</p>
            <p className="text-sm">{company.phone}</p>
            <p className="text-sm">{company.email}</p>
          </div>
          <div>
            <p className="label-uppercase text-xs text-[#4B5563]">{t("inv.customer")}</p>
            <p className="font-bold mt-1">{user.full_name}</p>
            <p className="text-sm">{user.email}</p>
            {user.phone && <p className="text-sm">{user.phone}</p>}
            {user.city && <p className="text-sm">{user.city}{user.country ? `, ${user.country}` : ""}</p>}
          </div>
        </div>

        <div className="border-t border-b border-[#1A3322]/15 py-4 mb-6">
          <div className="grid grid-cols-12 text-xs font-bold uppercase tracking-wider text-[#4B5563]">
            <div className="col-span-6">{t("inv.designation")}</div>
            <div className="col-span-2 text-center">{t("inv.qty")}</div>
            <div className="col-span-2 text-right">{t("inv.unit_price")}</div>
            <div className="col-span-2 text-right">{t("inv.total")}</div>
          </div>
        </div>

        <div className="space-y-2 mb-8" data-testid="invoice-items">
          {order.items.map((it) => (
            <div key={`${it.kind}-${it.item_id}`} className="grid grid-cols-12 text-sm">
              <div className="col-span-6">
                <p className="font-semibold">{it.title}</p>
                <p className="text-xs text-[#4B5563] uppercase">{it.kind === "product" ? t("inv.product") : t("inv.service")}</p>
              </div>
              <div className="col-span-2 text-center">{it.quantity}</div>
              <div className="col-span-2 text-right">{it.price.toFixed(2)} €</div>
              <div className="col-span-2 text-right font-semibold">{(it.price * it.quantity).toFixed(2)} €</div>
            </div>
          ))}
        </div>

        <div className="ml-auto w-64 border-t border-[#1A3322]/15 pt-4 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-[#4B5563]">{t("inv.subtotal")}</span><span>{subtotal.toFixed(2)} €</span></div>
          {order.shipping_fee_eur > 0 && (
            <div className="flex justify-between" data-testid="invoice-shipping">
              <span className="text-[#4B5563]">{order.shipping_name || t("inv.shipping")}</span>
              <span>+ {order.shipping_fee_eur.toFixed(2)} €</span>
            </div>
          )}
          {order.coupon && (
            <div className="flex justify-between text-[#C85A32]" data-testid="invoice-coupon">
              <span>{t("inv.coupon")} <span className="font-mono">{order.coupon.code}</span></span>
              <span>- {order.coupon.discount_eur.toFixed(2)} €</span>
            </div>
          )}
          <div className="flex justify-between border-t border-[#1A3322]/15 pt-2 mt-2">
            <span className="heading font-bold text-lg">{t("inv.total_ttc")}</span>
            <span className="heading font-extrabold text-lg text-[#1A3322]" data-testid="invoice-total">{order.total.toFixed(2)} €</span>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[#1A3322]/10 text-xs text-[#4B5563]">
          <p><strong>{t("inv.pay_method")}</strong> {order.provider === "bank_transfer" ? `${t("inv.pay_bank_ref")} ${order.reference})` : order.provider === "pawapay" ? t("inv.pay_pawapay") : t("inv.pay_card")}</p>
          <p className="mt-1">{t("inv.no_vat")}</p>
          <p className="mt-3 italic">{t("inv.thanks_pre")} SUKANACOOP.</p>
        </div>
      </div>
    </div>
  );
}
